import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { BehaviorSubject, Observable } from 'rxjs'
import { tap } from 'rxjs/operators'
import { jwtDecode } from 'jwt-decode'
import { AuthenticationResult } from '@azure/msal-browser'
import { environment } from 'src/environments/environment'

export interface MappingGroup {
  id: string
  name: string
  role: string
}

@Injectable({
  providedIn: 'root',
})
export class AzureService {
  // Admin group IDs (only for admin detection) - dev
  private readonly ADMIN_GROUP_IDS_DEV = [
    '6343d8b9-013c-4bea-a6aa-a1a3cba074bf',	//Ascent RME CP Manager - Dev
    '8c06070b-f030-4ecf-8794-b5951df79ec1',	//Ascent RME STG Manager - Dev
    '491b59fc-b77e-4f35-a93d-cc3cbbcc777f',	//Ascent APAC CP Manager - Dev
    '325035ba-782b-4806-bb3e-e56ad1a17557',	//Ascent APAC STG Manager - Dev
    'b31e3916-f8ae-4236-bc5e-91c5e3beac44',	//Ascent KSA CP Manager - Dev
  ]

  // Allowed role groups - dev
  private readonly ALLOWED_ROLE_GROUP_IDS_DEV = [
    '6343d8b9-013c-4bea-a6aa-a1a3cba074bf',	//Ascent RME CP Manager - Dev
    '3a08902b-c275-4f09-9a58-b8fa77b144e6',	//Ascent RME CP Sales - Dev
    '8c06070b-f030-4ecf-8794-b5951df79ec1',	//Ascent RME STG Manager - Dev
    '4de8f616-bc63-43f6-9f88-7c3d3fcccd38',	//Ascent RME STG Sales - Dev
    '491b59fc-b77e-4f35-a93d-cc3cbbcc777f',	//Ascent APAC CP Manager - Dev
    'a2223386-dd6b-4ca2-8e3c-8d8704bbaba9',	//Ascent APAC CP Sales - Dev
    '325035ba-782b-4806-bb3e-e56ad1a17557',	//Ascent APAC STG Manager - Dev
    'add52305-06b9-46b7-b78c-970bd51c3966',	//Ascent APAC STG Sales - Dev
    'b31e3916-f8ae-4236-bc5e-91c5e3beac44',	//Ascent KSA CP Manager - Dev
    'f9f139a6-16aa-4f82-8357-fe879d980ae2',	//Ascent KSA CP Sales - Dev
  ]

  // Admin group IDs (only for admin detection) - prod
  private readonly ADMIN_GROUP_IDS = [
    'eba71cd2-0d6e-4136-9746-f8ffba5ec0c1', //Ascent RME CP Manager
    '19c79f09-bb8a-4b5e-bb5f-2fa8936dfb35', //Ascent RME STG Manager
    'aa66a4a1-14e9-49c8-a9e0-4ea386423565', //Ascent APAC CP Manager
    'c868588d-0c42-46ef-98d3-6aeabc4341f3',	//Ascent APAC STG Manager
    '81f97122-2bcf-4134-b385-8db539a59cdb',	//Ascent KSA CP Manager
  ]

  // Allowed role groups - prod
  private readonly ALLOWED_ROLE_GROUP_IDS = [
    'eba71cd2-0d6e-4136-9746-f8ffba5ec0c1', //Ascent RME CP Manager
    '1fc3f32d-a3e3-469f-b82b-c35a3d774bb6', //Ascent RME CP Sales
    '19c79f09-bb8a-4b5e-bb5f-2fa8936dfb35', //Ascent RME STG Manager
    '9e5b5641-ba51-4207-b555-4bdbd7710cd3', //Ascent RME STG Sales,
    'aa66a4a1-14e9-49c8-a9e0-4ea386423565', //Ascent APAC CP Manager
    '8a408319-b955-44c4-966b-aff1377613d8', //Ascent APAC CP Sales
    'c868588d-0c42-46ef-98d3-6aeabc4341f3', //Ascent APAC STG Manager
    'e95078ea-6117-4470-a9ad-5d0a3aa916a1', //Ascent APAC STG Sales
    '81f97122-2bcf-4134-b385-8db539a59cdb', //Ascent KSA CP Manager
    '5decc43c-a566-4ebf-8b87-e3bde2165860', //Ascent KSA CP Sales
  ]

  private currentUserSubject = new BehaviorSubject<any>(null)
  authServiceInstance: any
  token = new BehaviorSubject<any>(null)
  rolesSubject = new BehaviorSubject<any>([]);
  selectedRole = new BehaviorSubject<string>('')
  selectedBusinessArea = new BehaviorSubject<string>('')
  isProduction = false;

  constructor(private http: HttpClient) {
    const env = environment as any;
    this.isProduction = env.production;
  }

  getUserInfo(): Observable<any> {
    const url = 'https://graph.microsoft.com/v1.0/me'
    return this.http.get(url).pipe(
      tap((userData) => {
        this.currentUserSubject.next(userData)
      })
    )
  }

  getCurrentUser(): Observable<any> {
    return this.currentUserSubject.asObservable()
  }

  getCurrentUserValue(): any {
    return this.currentUserSubject.value
  }

  setAuthServiceInstance(authService: any) {
    this.authServiceInstance = authService
  }

  getAuthServiceInstance() {
    return this.authServiceInstance
  }

  async getAccessToken(): Promise<any> {
    if (!this.authServiceInstance) {
      throw new Error('Auth service instance is not set.')
    }

    try {
      const tokenResponse: AuthenticationResult = await this.authServiceInstance
        .acquireTokenSilent({
          scopes: [
            `${this.authServiceInstance.instance.controller.config.auth.clientId}/.default`,
          ],
        })
        .toPromise()

      if (tokenResponse) {
        const idTokenDecoded = jwtDecode(tokenResponse.idToken)
        return {
          accessToken: tokenResponse.accessToken,
          idToken: tokenResponse.idToken,
          idTokenDecoded: idTokenDecoded,
          groupIds: [...new Set((tokenResponse.idTokenClaims as any).groups)],
        }
      }
    } catch (error) {
      console.error('Error fetching Token:', error)
      throw error
    }
  }

  logout() {
    this.authServiceInstance.logoutRedirect({
      postLogoutRedirectUri: '/',
    })
    this.authServiceInstance = null
    this.currentUserSubject.next(null)
    localStorage.removeItem('role')
  }

async getRoles(): Promise<MappingGroup[]> {

  if (this.rolesSubject.value.length > 0) {
    console.log('[Roles] Returning cached roles:', this.rolesSubject.value);
    return this.rolesSubject.value;
  }

    try {
      let memberUrl = 'https://graph.microsoft.com/v1.0/me/memberOf';
      let memberGroups: any[] = [];

      while (memberUrl) {
        const response: any = await this.http.get(memberUrl).toPromise();
        const values = response?.value || [];
        memberGroups = [...memberGroups, ...values];
        console.log('[Roles] Page memberOf groups:', values.length);
        memberUrl = response['@odata.nextLink'] || null;
      }

      let ownedUrl = 'https://graph.microsoft.com/v1.0/me/ownedObjects';
      let ownedObjects: any[] = [];

      while (ownedUrl) {
        const response: any = await this.http.get(ownedUrl).toPromise();
        const values = response?.value || [];
        ownedObjects = [...ownedObjects, ...values];
        console.log('[Roles] Page ownedObjects:', values.length);
        ownedUrl = response['@odata.nextLink'] || null;
      }

      console.log('[Roles] Total memberOf:', memberGroups.length);
      console.log('[Roles] Total ownedObjects:', ownedObjects.length);

      const memberOfGroups = memberGroups.filter(
        (item: any) => item['@odata.type'] === '#microsoft.graph.group'
      );

      const ownedGroups = ownedObjects.filter(
        (item: any) => item['@odata.type'] === '#microsoft.graph.group'
      );


      const combinedGroups = [...memberOfGroups, ...ownedGroups];
      const uniqueGroups = Array.from(
        new Map(combinedGroups.map(g => [g.id, g])).values()
      );

      console.log('[Roles] Unique combined groups:', uniqueGroups.length);

      let roleFiltered = [];
      if (this.isProduction) {
        roleFiltered = uniqueGroups.filter((group: any) =>
          this.ALLOWED_ROLE_GROUP_IDS.includes(group.id)
        )
      } else {
        roleFiltered = uniqueGroups.filter((group: any) =>
          this.ALLOWED_ROLE_GROUP_IDS_DEV.includes(group.id)
        )
      }

      console.log('[Roles] After allowed role filter:', roleFiltered.length);

      const roles: MappingGroup[] = roleFiltered.map((group: any) => ({
        id: group.id,
        name: group.displayName,
        role: group.displayName,
      }));

      console.log('[Roles] Final roles:', roles);

      this.rolesSubject.next(roles);
      return roles;

    } catch (error) {
      console.error('[Roles] Error fetching roles from Microsoft Graph API:', error);
      this.rolesSubject.next([]);
      return [];
    }
  }

  getRolesObservable(): Observable<string> {
    return this.rolesSubject.asObservable();
  }

  isAdminRole(roleId: string): boolean {
    return this.isProduction ? this.ADMIN_GROUP_IDS.includes(roleId) : this.ADMIN_GROUP_IDS_DEV.includes(roleId);
  }
  
}
